/**
 * FASE 22.1 — WORKFLOW SCHEMAS
 *
 * Typed definitions for every workflow Kevia can manage.
 * Includes step templates and lifecycle metadata.
 */

export const WORKFLOW_TYPES = Object.freeze({
  FAMILY_FOLLOWUP:           "family_followup",
  BAPTISM_PREPARATION:       "baptism_preparation",
  PRAYER_FOLLOWUP:           "prayer_followup",
  INACTIVE_MEMBER_RECOVERY:  "inactive_member_recovery",
  VISIT_CYCLE:               "visit_cycle",
  EVENT_PREPARATION:         "event_preparation",
});

export const WORKFLOW_STATUS = Object.freeze({
  ACTIVE:    "active",
  PAUSED:    "paused",
  COMPLETED: "completed",
  ARCHIVED:  "archived",
});

export const STEP_STATUS = Object.freeze({
  PENDING:     "pending",
  IN_PROGRESS: "in_progress",
  DONE:        "done",
  SKIPPED:     "skipped",
});

export const PRIORITY = Object.freeze({
  LOW:      "low",
  MEDIUM:   "medium",
  HIGH:     "high",
  CRITICAL: "critical",
});

/**
 * @typedef {Object} WorkflowStep
 * @property {string} id
 * @property {string} label
 * @property {string} status — STEP_STATUS
 * @property {number|null} completed_at
 */

/**
 * @typedef {Object} RelatedEntity
 * @property {string} type — "family"|"person"|"church"|"prayer"|"event"
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} Workflow
 * @property {string}          id
 * @property {string}          type           — WORKFLOW_TYPES
 * @property {string}          status         — WORKFLOW_STATUS
 * @property {string}          priority       — PRIORITY
 * @property {number}          created_at     — ms timestamp
 * @property {number}          updated_at
 * @property {RelatedEntity[]} related_entities
 * @property {WorkflowStep[]}  steps
 * @property {number}          progress       — 0–1
 * @property {number}          last_activity  — ms timestamp
 * @property {string|null}     next_recommended_action
 */

/** Workflow type metadata + step templates. */
export const WORKFLOW_DEFINITIONS = Object.freeze({

  [WORKFLOW_TYPES.FAMILY_FOLLOWUP]: {
    label:            "Seguimiento familiar",
    description:      "Seguimiento pastoral continuo de una familia",
    defaultPriority:  PRIORITY.MEDIUM,
    maxInactiveDays:  21,
    stepTemplates:    [
      { id: "initial_contact",  label: "Contacto inicial" },
      { id: "first_visit",      label: "Primera visita" },
      { id: "prayer_register",  label: "Registrar oración" },
      { id: "followup_visit",   label: "Visita de seguimiento" },
      { id: "review",           label: "Revisión de estado" },
    ],
  },

  [WORKFLOW_TYPES.BAPTISM_PREPARATION]: {
    label:            "Preparación para bautismo",
    description:      "Acompañamiento de un candidato al bautismo",
    defaultPriority:  PRIORITY.HIGH,
    maxInactiveDays:  14,
    stepTemplates:    [
      { id: "interview",       label: "Entrevista inicial" },
      { id: "classes_start",   label: "Inicio de clases" },
      { id: "classes_done",    label: "Clases completadas" },
      { id: "pastor_approval", label: "Aprobación pastoral" },
      { id: "baptism_done",    label: "Bautismo realizado" },
    ],
  },

  [WORKFLOW_TYPES.PRAYER_FOLLOWUP]: {
    label:            "Seguimiento de oración",
    description:      "Seguimiento de una solicitud de oración activa",
    defaultPriority:  PRIORITY.MEDIUM,
    maxInactiveDays:  10,
    stepTemplates:    [
      { id: "prayer_registered", label: "Oración registrada" },
      { id: "intercession",      label: "Intercesión realizada" },
      { id: "update_contact",    label: "Actualización con la persona" },
      { id: "resolution",        label: "Resolución o cierre" },
    ],
  },

  [WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY]: {
    label:            "Recuperación de miembro inactivo",
    description:      "Acompañamiento pastoral de un miembro distanciado",
    defaultPriority:  PRIORITY.HIGH,
    maxInactiveDays:  30,
    stepTemplates:    [
      { id: "initial_reach",  label: "Contacto inicial" },
      { id: "home_visit",     label: "Visita al hogar" },
      { id: "counseling",     label: "Consejería pastoral" },
      { id: "reintegration",  label: "Reintegración a la iglesia" },
    ],
  },

  [WORKFLOW_TYPES.VISIT_CYCLE]: {
    label:            "Ciclo de visitas",
    description:      "Ciclo periódico de visitas pastorales",
    defaultPriority:  PRIORITY.LOW,
    maxInactiveDays:  30,
    stepTemplates:    [
      { id: "plan",      label: "Planificar visitas" },
      { id: "execute",   label: "Realizar visitas" },
      { id: "register",  label: "Registrar resultados" },
      { id: "review",    label: "Revisar ciclo" },
    ],
  },

  [WORKFLOW_TYPES.EVENT_PREPARATION]: {
    label:            "Preparación de evento",
    description:      "Coordinación de un evento pastoral o de iglesia",
    defaultPriority:  PRIORITY.MEDIUM,
    maxInactiveDays:  7,
    stepTemplates:    [
      { id: "planning",      label: "Planificación" },
      { id: "communication", label: "Comunicación" },
      { id: "logistics",     label: "Logística" },
      { id: "execution",     label: "Realización" },
      { id: "followup",      label: "Seguimiento post-evento" },
    ],
  },
});

export function getWorkflowDefinition(type) {
  return WORKFLOW_DEFINITIONS[type] ?? null;
}

/** Build initial steps from a workflow type definition. */
export function buildInitialSteps(type) {
  const def = getWorkflowDefinition(type);
  if (!def) return [];
  return def.stepTemplates.map((t) => ({
    id:           t.id,
    label:        t.label,
    status:       STEP_STATUS.PENDING,
    completed_at: null,
  }));
}

/** Compute progress from steps array (0–1). */
export function computeProgress(steps) {
  if (!steps?.length) return 0;
  const done = steps.filter((s) => s.status === STEP_STATUS.DONE || s.status === STEP_STATUS.SKIPPED).length;
  return Math.round((done / steps.length) * 100) / 100;
}
